import * as dicomParser from 'dicom-parser';
// healthImagingService.js
import {
  MedicalImagingClient,
  SearchImageSetsCommand,
  GetImageSetMetadataCommand,
  GetImageFrameCommand,
  ListDatastoresCommand
} from "@aws-sdk/client-medical-imaging";

// Initialize the AWS HealthImaging client
// In production, use AWS Cognito or IAM roles instead of hardcoded credentials
const createHealthImagingClient = () => {
  return new MedicalImagingClient({
    region: process.env.REACT_APP_AWS_REGION || "us-east-1",
    credentials: {
      accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      sessionToken: process.env.REACT_APP_AWS_SESSION_TOKEN // Optional, for temporary credentials
    }
  });
};

// Service class for all HealthImaging operations
class HealthImagingService {
  constructor() {
    this.client = createHealthImagingClient();
    this.datastoreId = process.env.REACT_APP_HEALTHIMAGING_DATASTORE_ID;
  }

  // List all available datastores
  async listDatastores() {
    try {
      const command = new ListDatastoresCommand({});
      const response = await this.client.send(command);
      return response.datastoreSummaries;
    } catch (error) {
      console.error("Error listing datastores:", error);
      throw error;
    }
  }

  // Search for image sets (studies) with filters
  async searchStudies(searchParams = {}) {
    try {
      // Check if datastore ID is configured
      if (!this.datastoreId) {
        throw new Error('HEALTHIMAGING_DATASTORE_ID not configured in environment variables');
      }

      const {
        patientId,
        patientName,
        studyDateFrom,
        studyDateTo,
        modality,
        maxResults = 50,
        nextToken
      } = searchParams;

      // Build search criteria
      const searchCriteria = {
        filters: []
      };

      // Add filters based on provided parameters
      if (patientId) {
        searchCriteria.filters.push({
          values: [{ DICOMPatientId: patientId }],
          operator: "EQUAL"
        });
      }

      if (studyDateFrom || studyDateTo) {
        const dateFilter = {
          values: [{}]
        };
        if (studyDateFrom) {
          dateFilter.values[0].createdAt = studyDateFrom;
          dateFilter.operator = "BETWEEN";
        }
        if (studyDateTo && studyDateFrom) {
          dateFilter.values.push({ createdAt: studyDateTo });
        }
        searchCriteria.filters.push(dateFilter);
      }

      const command = new SearchImageSetsCommand({
        datastoreId: this.datastoreId,
        searchCriteria: {
          filters: searchCriteria.filters.length > 0 ? searchCriteria.filters : undefined,
          sort: {
            sortOrder: "DESC",
            sortField: "updatedAt"
          }
        },
        maxResults,
        nextToken
      });

      console.log('Searching with datastoreId:', this.datastoreId);
      console.log('Search command:', command);

      const response = await this.client.send(command);

      console.log('AWS HealthImaging response:', response);

      // Check if we have imageSetsMetadataSummaries in the response
      if (!response.imageSetsMetadataSummaries) {
        console.warn('No imageSetsMetadataSummaries in response, returning empty array');
        return {
          studies: [],
          nextToken: response.nextToken
        };
      }

      // Transform the response to match our DataGrid format
      const studies = response.imageSetsMetadataSummaries.map((imageSet) => {
        const dicomTags = imageSet.DICOMTags || {};

        return {
          id: imageSet.imageSetId,
          patientId: dicomTags.DICOMPatientId || 'Unknown',
          patientName: this.formatPatientName(dicomTags.DICOMPatientName),
          studyDate: this.formatStudyDate(dicomTags.DICOMStudyDate),
          studyTime: this.formatStudyTime(dicomTags.DICOMStudyTime),
          modality: dicomTags.DICOMSeriesModality || '',
          studyDescription: dicomTags.DICOMStudyDescription || 'No description',
          accessionNumber: dicomTags.DICOMAccessionNumber || '',
          numberOfImages: dicomTags.DICOMNumberOfStudyRelatedInstances || 0,
          studyInstanceUID: dicomTags.DICOMStudyInstanceUID || '',
          status: 'ACTIVE', // Status is not in the summary
          referringPhysician: '', // Not available in summary
          imageSetVersion: imageSet.version,
          createdAt: imageSet.createdAt,
          updatedAt: imageSet.updatedAt,
          seriesInstanceUID: dicomTags.DICOMSeriesInstanceUID,
          patientBirthDate: dicomTags.DICOMPatientBirthDate,
          patientSex: dicomTags.DICOMPatientSex,
          studyId: dicomTags.DICOMStudyId,
          seriesBodyPart: dicomTags.DICOMSeriesBodyPart,
          seriesNumber: dicomTags.DICOMSeriesNumber,
          numberOfSeries: dicomTags.DICOMNumberOfStudyRelatedSeries
        };
      });

      return {
        studies,
        nextToken: response.nextToken
      };
    } catch (error) {
      console.error("Error searching studies:", error);
      throw error;
    }
  }

  // Get detailed metadata for a specific study
  async getStudyMetadata(imageSetId, versionId = null) {
    try {
      const params = {
        datastoreId: this.datastoreId,
        imageSetId: imageSetId
      };

      if (versionId) {
        params.versionId = versionId;
      }

      const command = new GetImageSetMetadataCommand(params);
      const response = await this.client.send(command);

      console.log('Metadata response:', response);

      // The metadata blob might be a Uint8Array or other format
      let metadata;
      if (response.imageSetMetadataBlob instanceof Uint8Array) {
        // If it's already a Uint8Array, decode it directly
        const metadataString = new TextDecoder().decode(response.imageSetMetadataBlob);
        metadata = JSON.parse(metadataString);
      } else if (response.imageSetMetadataBlob instanceof ReadableStream) {
        // If it's a stream, we need to read it first
        const reader = response.imageSetMetadataBlob.getReader();
        const chunks = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }

        // Combine chunks into a single Uint8Array
        const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }

        const metadataString = new TextDecoder().decode(combined);
        metadata = JSON.parse(metadataString);
      } else {
        // Try to handle it as-is
        metadata = JSON.parse(response.imageSetMetadataBlob);
      }

      return metadata;
    } catch (error) {
      console.error("Error getting study metadata:", error);
      throw error;
    }
  }

  // Get image frame data
  async getImageFrame(imageSetId, imageFrameId) {
    try {
      const command = new GetImageFrameCommand({
        datastoreId: this.datastoreId,
        imageSetId: imageSetId,
        imageFrameInformation: {
          imageFrameId: imageFrameId
        }
      });

      const response = await this.client.send(command);

      // The response includes the image frame as a blob
      return {
        imageBlob: response.imageFrameBlob,
        contentType: response.contentType
      };
    } catch (error) {
      console.error("Error getting image frame:", error);
      throw error;
    }
  }

  // Get all image frames for a study
  async getStudyImages(imageSetId) {
    try {
      const metadata = await this.getStudyMetadata(imageSetId);
      const imageFrames = [];

      console.log('Processing metadata for study:', imageSetId);

      // Iterate through the study structure
      if (metadata.Study && metadata.Study.Series) {
        for (const [seriesId, series] of Object.entries(metadata.Study.Series)) {
          if (series.Instances) {
            for (const [instanceId, instance] of Object.entries(series.Instances)) {
              // Check if this is a multiframe instance
              const numberOfFrames = parseInt(instance.DICOM?.NumberOfFrames || '1');
              const isMultiframe = numberOfFrames > 1;
              const transferSyntaxUID = instance.DICOM?.TransferSyntaxUID;

              console.log(`Instance ${instanceId}: ${numberOfFrames} frames, Transfer Syntax: ${transferSyntaxUID}`);

              if (instance.ImageFrames) {
                // ImageFrames is an array with frame objects
                if (Array.isArray(instance.ImageFrames)) {
                  instance.ImageFrames.forEach((frame, index) => {
                    console.log(`Frame ${index}:`, frame);
                    imageFrames.push({
                      imageFrameId: frame.ID, // Use the ID field from the frame
                      seriesInstanceUID: series.DICOM?.SeriesInstanceUID,
                      sopInstanceUID: instance.DICOM?.SOPInstanceUID,
                      instanceNumber: parseInt(instance.DICOM?.InstanceNumber || '0'),
                      frameNumber: index + 1,
                      seriesDescription: series.DICOM?.SeriesDescription || '',
                      seriesNumber: parseInt(series.DICOM?.SeriesNumber || '0'),
                      imagePositionPatient: instance.DICOM?.ImagePositionPatient,
                      imageOrientationPatient: instance.DICOM?.ImageOrientationPatient,
                      pixelSpacing: frame.PixelSpacing,
                      rows: frame.Height || instance.DICOM?.Rows,
                      columns: frame.Width || instance.DICOM?.Columns,
                      windowCenter: instance.DICOM?.WindowCenter,
                      windowWidth: instance.DICOM?.WindowWidth,
                      // Add multiframe info
                      isMultiframe: isMultiframe,
                      numberOfFrames: numberOfFrames,
                      transferSyntaxUID: transferSyntaxUID,
                      // Store the entire DICOM metadata for the file
                      dicomMetadata: instance.DICOM
                    });
                  });
                } else {
                  // ImageFrames is an object with frame IDs as keys
                  for (const [frameIndex, frame] of Object.entries(instance.ImageFrames)) {
                    console.log(`Frame index ${frameIndex}:`, frame);

                    // For objects, the frame data is in the value, and ID might be a property
                    const frameId = frame.ID || frameIndex; // Use ID if available, otherwise use the key

                    imageFrames.push({
                      imageFrameId: frameId, // This should be the 32-character ID
                      seriesInstanceUID: series.DICOM?.SeriesInstanceUID,
                      sopInstanceUID: instance.DICOM?.SOPInstanceUID,
                      instanceNumber: parseInt(instance.DICOM?.InstanceNumber || '0'),
                      frameNumber: 1, // Single frame per file for multiframe MP4s
                      seriesDescription: series.DICOM?.SeriesDescription || '',
                      seriesNumber: parseInt(series.DICOM?.SeriesNumber || '0'),
                      imagePositionPatient: instance.DICOM?.ImagePositionPatient,
                      imageOrientationPatient: instance.DICOM?.ImageOrientationPatient,
                      pixelSpacing: frame.PixelSpacing,
                      rows: frame.Height || instance.DICOM?.Rows,
                      columns: frame.Width || instance.DICOM?.Columns,
                      windowCenter: instance.DICOM?.WindowCenter,
                      windowWidth: instance.DICOM?.WindowWidth,
                      // Add multiframe info
                      isMultiframe: isMultiframe,
                      numberOfFrames: numberOfFrames,
                      transferSyntaxUID: transferSyntaxUID,
                      // Store the entire DICOM metadata for the file
                      dicomMetadata: instance.DICOM
                    });
                  }
                }
              }
            }
          }
        }
      }

      console.log(`Total frames found: ${imageFrames.length}`);
      imageFrames.forEach((frame, index) => {
        console.log(`Frame ${index}: ID=${frame.imageFrameId}, Instance=${frame.instanceNumber}, Multiframe=${frame.isMultiframe}`);
      });

      // Sort by series number and instance number
      imageFrames.sort((a, b) => {
        if (a.seriesNumber !== b.seriesNumber) {
          return a.seriesNumber - b.seriesNumber;
        }
        return a.instanceNumber - b.instanceNumber;
      });

      return imageFrames;
    } catch (error) {
      console.error("Error getting study images:", error);
      throw error;
    }
  }

  // Download image frame as DICOM file
  async downloadImageAsDicom(imageSetId, imageFrameId) {
    try {
      const imageData = await this.getImageFrame(imageSetId, imageFrameId);

      // Create a blob and download link
      const blob = new Blob([imageData.imageBlob], { type: imageData.contentType });
      const url = URL.createObjectURL(blob);

      return url;
    } catch (error) {
      console.error("Error downloading image:", error);
      throw error;
    }
  }

  // Note: This method is kept for backwards compatibility but should not be used with the viewer
  // The viewer should use the ahiImageLoader.js downloadStudyForViewing() method instead
  async downloadStudyAsFiles(imageSetId) {
    console.warn('downloadStudyAsFiles is deprecated for viewing. Use downloadStudyForViewing from ahiImageLoader.js instead.');
    throw new Error('This method should not be used for viewing. Use downloadStudyForViewing from ahiImageLoader.js instead.');
  }

  // Note: This method is no longer needed when using the ahiImageLoader
  // Kept for reference only
  wrapPixelDataAsDICOM(pixelData, frameMetadata) {
    console.warn('wrapPixelDataAsDICOM is deprecated. Use ahiImageLoader.js for viewing AHI images.');
    // Return the pixel data as-is since we're not creating DICOM files anymore
    return pixelData;
  }

  // Helper methods
  formatPatientName(dicomName) {
    if (!dicomName) return 'Unknown';
    // DICOM names are in format: LastName^FirstName^MiddleName^Prefix^Suffix
    const parts = dicomName.split('^');
    if (parts.length >= 2) {
      return `${parts[0]}, ${parts[1]}`;
    }
    return dicomName.replace(/\^/g, ' ');
  }

  formatStudyDate(dicomDate) {
    if (!dicomDate || dicomDate.length !== 8) {
      console.log('Invalid DICOM date:', dicomDate);
      return '';
    }
    // DICOM date format: YYYYMMDD
    const year = dicomDate.substring(0, 4);
    const month = dicomDate.substring(4, 6);
    const day = dicomDate.substring(6, 8);
    const formatted = `${year}-${month}-${day}`;
    console.log('Formatted date:', dicomDate, '->', formatted);
    return formatted;
  }

  formatStudyTime(dicomTime) {
    if (!dicomTime || dicomTime.length < 6) return '';
    // DICOM time format: HHMMSS.FFFFFF
    const hour = dicomTime.substring(0, 2);
    const minute = dicomTime.substring(2, 4);
    return `${hour}:${minute}`;
  }

  extractModality(metadata) {
    // Try to get modality from the first series
    if (metadata.Study && metadata.Study.Series) {
      const firstSeries = Object.values(metadata.Study.Series)[0];
      return firstSeries?.DICOM?.Modality || '';
    }
    return '';
  }

  countImages(metadata) {
    let count = 0;
    if (metadata.Study && metadata.Study.Series) {
      for (const series of Object.values(metadata.Study.Series)) {
        if (series.Instances) {
          for (const instance of Object.values(series.Instances)) {
            if (instance.ImageFrames) {
              count += Object.keys(instance.ImageFrames).length;
            }
          }
        }
      }
    }
    return count;
  }
}

// Export a singleton instance
const healthImagingService = new HealthImagingService();
export default healthImagingService;